import { format, formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Container,
  ExternalLink,
  Lock,
  Timer,
  XCircle,
} from 'lucide-react'
import { useSystemOverview, useMaintenanceLog } from '@/hooks/useApi'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

/**
 * The operational view: what runs, on what, and when it last ran.
 *
 * Each block was somewhere it did not belong — health at the bottom of
 * Settings, container digests as editable settings rows, the beat schedule
 * only in source, maintenance events mixed into the alert stream. None of it
 * is configuration; all of it is what you check when something looks off.
 */

function shortDigest(digest: string | null): string {
  if (!digest) return '—'
  const hash = digest.split('@sha256:')[1]
  return hash ? `sha256:${hash.slice(0, 12)}` : digest
}

export default function System() {
  const { data, isLoading } = useSystemOverview()
  const { data: maintenance } = useMaintenanceLog()

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  const services = data?.services
  const staleTasks = (data?.scheduled ?? []).filter((s) => s.stale).length

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Systeem</h1>
        <p className="text-muted-foreground">
          Services, images, takenschema en onderhoudslog
        </p>
      </div>

      {/* ── Services ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            Services
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {[
            ['PostgreSQL', services?.database],
            ['Redis', services?.redis],
          ].map(([label, ok]) => (
            <div
              key={String(label)}
              className="flex items-center gap-2 rounded-lg border px-4 py-2.5"
            >
              {ok ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
              <span className="font-medium">{label}</span>
              <span className={cn('text-sm', ok ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300')}>
                {ok ? 'actief' : 'fout'}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Images ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Container className="h-4 w-4 text-muted-foreground" />
            Container-images
          </CardTitle>
          <CardDescription>
            Wekelijks gecontroleerd op digest-wijzigingen. Een image op een
            zwevende tag kan onder je voeten veranderen; een gepinde niet.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-xs text-muted-foreground">
                  <th className="px-3 py-2 text-left font-medium">Naam</th>
                  <th className="px-3 py-2 text-left font-medium">Referentie</th>
                  <th className="px-3 py-2 text-left font-medium">Digest</th>
                  <th className="px-3 py-2 text-right font-medium">Bijgewerkt</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(data?.images ?? []).map((img) => (
                  <tr key={img.name}>
                    <td className="px-3 py-2 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {img.pinned && (
                          <Lock className="h-3 w-3 text-green-600 dark:text-green-300" aria-label="gepind" />
                        )}
                        {img.name}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {img.reference}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{shortDigest(img.digest)}</td>
                    <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                      {img.digest_updated_at
                        ? formatDistanceToNow(new Date(img.digest_updated_at), {
                            addSuffix: true,
                            locale: nl,
                          })
                        : 'nog niet vastgelegd'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ── Beat schedule ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-4 w-4 text-muted-foreground" />
            Takenschema ({data?.scheduled.length ?? 0})
            {staleTasks > 0 && (
              <Badge variant="destructive">{staleTasks} achterstallig</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Wat Celery beat inplant. "Laatste run" komt uit de takenlog en is
            leeg voor taken die daar niet in schrijven — dat betekent onbekend,
            niet mislukt.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-xs text-muted-foreground">
                  <th className="px-3 py-2 text-left font-medium">Taak</th>
                  <th className="px-3 py-2 text-left font-medium">Schema</th>
                  <th className="px-3 py-2 text-right font-medium">Laatste run</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(data?.scheduled ?? []).map((task) => (
                  <tr key={task.key} className={task.stale ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{task.key}</div>
                      <div className="font-mono text-xs text-muted-foreground">{task.task}</div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{task.schedule}</td>
                    <td className="px-3 py-2 text-right text-xs">
                      {task.last_run_at ? (
                        <span
                          className={cn(
                            'inline-flex items-center gap-1',
                            task.stale ? 'font-medium text-red-600 dark:text-red-300' : 'text-muted-foreground',
                          )}
                        >
                          {task.stale && <AlertTriangle className="h-3 w-3" />}
                          {formatDistanceToNow(new Date(task.last_run_at), {
                            addSuffix: true,
                            locale: nl,
                          })}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">onbekend</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ── Maintenance log ── */}
      <Card>
        <CardHeader>
          <CardTitle>Onderhoudslog</CardTitle>
          <CardDescription>
            Gebeurtenissen die geregistreerd worden maar geen actie vragen.
            Stonden eerder als info-alerts tussen de CVE's.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!maintenance?.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nog niets geregistreerd.
            </p>
          ) : (
            <div className="space-y-2">
              {maintenance.map((entry) => (
                <div key={entry.id} className="rounded-lg border p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{entry.detail ?? entry.subject}</span>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(entry.occurred_at), 'd MMM yyyy HH:mm', { locale: nl })}
                    </span>
                  </div>
                  {entry.old_value && entry.new_value && (
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {shortDigest(entry.old_value)} → {shortDigest(entry.new_value)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Links ── */}
      <Card>
        <CardHeader>
          <CardTitle>Snelkoppelingen</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {Object.entries(data?.links ?? {}).map(([label, href]) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm capitalize transition-colors hover:bg-muted"
            >
              {label}
              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
            </a>
          ))}
          <p className="w-full text-xs text-muted-foreground">
            Flower staat niet in deze lijst: het luistert op <code className="rounded bg-muted px-1">127.0.0.1:5555</code> op
            de server en wordt niet door de reverse proxy doorgegeven. Bereik het via een SSH-tunnel:{' '}
            <code className="rounded bg-muted px-1">ssh -p 2222 -L 5555:127.0.0.1:5555 root@&lt;server&gt;</code>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
